import type { CancelablePromise } from "./core/CancelablePromise"
import { OpenAPI } from "./core/OpenAPI"
import { request as __request } from "./core/request"
import type {
  BodyLoginAccessToken,
  CalendarEventCreate,
  CalendarEventPublic,
  ChangeoverActiveBulkRequest,
  ChangeoverActiveBulkResponse,
  ChangeoverActiveRequest,
  ChangeoverApproveRequest,
  ChangeoverArchiveRequest,
  ChangeoverHistoryResponse,
  ChangeoverResponse,
  ChangeoverStartRequest,
  ChangeoverStepUpdateRequest,
  DahboardWydmuchWtryskResponse,
  DashboardOsobyDoDomuResponse,
  DashboardPeopleHome,
  DashedInfo,
  ExReceptionConfirmDTO,
  ExReceptionDTO,
  ExReceptionScanDTO,
  ExReceptionScanResponseDTO,
  ExReceptionsCreateDTO,
  ExReceptionsDTO,
  ExReceptionsUpdateDTO,
  ExReleaseConfirmDTO,
  ExReleaseDTO,
  ExReleasesCreateDTO,
  ExReleasesDTO,
  ExReleasesUpdateDTO,
  InternalExpenditureCreateRW,
  InternalExpenditureDocumentRWDetailDTO,
  InternalExpenditureDocumentRWDTO,
  InternalExpenditureDocumentRWResponseDTO,
  InternalExpenditureUpdateRW,
  InternalRevenueCreatePW,
  InternalRevenueDocumentPWDetailDTO,
  InternalRevenueDocumentPWDTO,
  InternalRevenueDocumentPWResponseDTO,
  InternalRevenueUpdatePW,
  MachinesResponceDTO,
  MagazynOsobyNaZmianie,
  MeldunekCreateDTO,
  MeldunekResponseDTO,
  Message,
  MmpDiscrepancyReportDTO,
  NewPassword,
  NewProductionActiveRequest,
  NewProductionApproveRequest,
  NewProductionGetByIdRequest,
  NewProductionResponse,
  NewProductionUpdateRequest,
  Permission,
  ProductCreateDTO,
  ProductionOrderCreateDTO,
  ProductionOrderListDTO,
  ProductionOrderResponseDTO,
  ProductionOrderUpdateDTO,
  ProductPurchasePricePointDTO,
  ProductRelatedDocDTO,
  ProductResponceDTO,
  ProductUpdateDTO,
  QualityControlActiveRequest,
  QualityControlApproveRequest,
  QualityControlResponse,
  QualityControlStartRequest,
  QualityControlUpdateItemRequest,
  QualityControlUpdateMetaRequest,
  ReportCopyInfo,
  ReportPublic,
  ReportRowUpdate,
  Role,
  SalesOrderConfirmDTO,
  SalesOrderDTO,
  SalesOrdersCreateDTO,
  SalesOrdersDTO,
  SalesOrdersUpdateDTO,
  TechnologyCreateDTO,
  TechnologyResponseDTO,
  TechnologyUpdateDTO,
  Token,
  TransferConfirmMMPDTO,
  TransferDocumentMMWByIDDTO,
  TransfersCreateMMW,
  TransfersDocumentMMPDTO,
  TransfersDocumentMMRDTO,
  TransfersDocumentMMWDTO,
  TransfersUpdateMMW,
  UpdatePassword,
  UpdateWarehouseDTO,
  UserCreate,
  UserPublic,
  UserRegister,
  UsersPublic,
  UserUpdate,
  UserUpdateMe,
  WarehouseCreateDTO,
  WarehouseLocationTemplateCreateDTO,
  WarehouseLocationTemplateDTO,
  WarehouseLocationTemplateUpdateDTO,
  WarehouseResponceDTO,
  WarehousesDTO,
  WtryskOsobyNaZmianie,
  WydmuchaOsobyNaZmianie,
  ZlecenieDetailResponse,
  ZlecenieSummaryDTO,
} from "./models"

export type TDataLoginAccessToken = {
  formData: BodyLoginAccessToken
}
export type TDataRecoverPassword = {
  email: string
}
export type TDataResetPassword = {
  requestBody: NewPassword
}
export type TDataRecoverPasswordHtmlContent = {
  email: string
}

export class LoginService {
  /**
   * Login Access Token
   * OAuth2 compatible token login, get an access token for future requests
   * @returns Token Successful Response
   * @throws ApiError
   */
  public static loginAccessToken(
    data: TDataLoginAccessToken,
  ): CancelablePromise<Token> {
    const { formData } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/login/access-token",
      body: formData,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Test Token
   * Test access token
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static testToken(): CancelablePromise<UserPublic> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/login/test-token",
    })
  }

  /**
   * Recover Password
   * Password Recovery
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static recoverPassword(
    data: TDataRecoverPassword,
  ): CancelablePromise<Message> {
    const { email } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/password-recovery/{email}",
      path: {
        email,
      },
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Reset Password
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static resetPassword(
    data: TDataResetPassword,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reset-password/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Recover Password Html Content
   * HTML Content for Password Recovery
   * @returns string Successful Response
   * @throws ApiError
   */
  public static recoverPasswordHtmlContent(
    data: TDataRecoverPasswordHtmlContent,
  ): CancelablePromise<string> {
    const { email } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/password-recovery-html-content/{email}",
      path: {
        email,
      },
      errors: {
        422: "Validation Error",
      },
    })
  }
}

export type TDataReadUsers = {
  limit?: number
  skip?: number
}
export type TDataCreateUser = {
  requestBody: UserCreate
}
export type TDataUpdateUserMe = {
  requestBody: UserUpdateMe
}
export type TDataUpdatePasswordMe = {
  requestBody: UpdatePassword
}
export type TDataRegisterUser = {
  requestBody: UserRegister
}
export type TDataReadUserById = {
  userId: string
}
export type TDataUpdateUser = {
  requestBody: UserUpdate
  userId: string
}
export type TDataDeleteUser = {
  userId: string
}

export class UsersService {
  /**
   * Read Users
   * Retrieve users.
   * @returns UsersPublic Successful Response
   * @throws ApiError
   */
  public static readUsers(
    data: TDataReadUsers = {},
  ): CancelablePromise<UsersPublic> {
    const { limit = 100, skip = 0 } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/users/",
      query: {
        skip,
        limit,
      },
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Create User
   * Create new user.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static createUser(
    data: TDataCreateUser,
  ): CancelablePromise<UserPublic> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/users/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Read User Me
   * Get current user.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static readUserMe(): CancelablePromise<UserPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/users/me",
    })
  }

  /**
   * Delete User Me
   * Delete own user.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteUserMe(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/users/me",
    })
  }

  /**
   * Update User Me
   * Update own user.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static updateUserMe(
    data: TDataUpdateUserMe,
  ): CancelablePromise<UserPublic> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/users/me",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Update Password Me
   * Update own password.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static updatePasswordMe(
    data: TDataUpdatePasswordMe,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/users/me/password",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Register User
   * Create new user without the need to be logged in.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static registerUser(
    data: TDataRegisterUser,
  ): CancelablePromise<UserPublic> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/users/signup",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Read User By Id
   * Get a specific user by id.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static readUserById(
    data: TDataReadUserById,
  ): CancelablePromise<UserPublic> {
    const { userId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/users/{user_id}",
      path: {
        user_id: userId,
      },
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Update User
   * Update a user.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static updateUser(
    data: TDataUpdateUser,
  ): CancelablePromise<UserPublic> {
    const { requestBody, userId } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/users/{user_id}",
      path: {
        user_id: userId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Delete User
   * Delete a user.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteUser(data: TDataDeleteUser): CancelablePromise<Message> {
    const { userId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/users/{user_id}",
      path: {
        user_id: userId,
      },
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Read Roles
   * Get all available roles.
   * @returns Role Successful Response
   * @throws ApiError
   */
  public static readRoles(): CancelablePromise<Array<Role>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/users/roles",
    })
  }

  public static createRole(requestBody: {
    code: string
    level: string
    permissions?: string[]
  }): CancelablePromise<Role> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/users/roles",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static updateRole(params: {
    roleId: number
    requestBody: { code: string; level: string; permissions?: string[] }
  }): CancelablePromise<Role> {
    const { roleId, requestBody } = params
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/users/roles/{roleId}",
      path: { roleId },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static deleteRole(params: { roleId: number }): CancelablePromise<any> {
    const { roleId } = params
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/users/roles/{roleId}",
      path: { roleId },
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static readPermissions(): CancelablePromise<Array<Permission>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/users/permissions",
    })
  }

  public static createPermission(requestBody: {
    code: string
    name: string
    description?: string
  }): CancelablePromise<Permission> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/users/permissions",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static updatePermission(params: {
    permissionId: number
    requestBody: { name: string; description?: string }
  }): CancelablePromise<Permission> {
    const { permissionId, requestBody } = params
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/users/permissions/{permissionId}",
      path: { permissionId },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static deletePermission(params: {
    permissionId: number
  }): CancelablePromise<any> {
    const { permissionId } = params
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/users/permissions/{permissionId}",
      path: { permissionId },
      errors: {
        422: "Validation Error",
      },
    })
  }
}

export type TDataTestEmail = {
  emailTo: string
}

export class UtilsService {
  /**
   * Test Email
   * Test emails.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static testEmail(data: TDataTestEmail): CancelablePromise<Message> {
    const { emailTo } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/utils/test-email/",
      query: {
        email_to: emailTo,
      },
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Health Check
   * @returns boolean Successful Response
   * @throws ApiError
   */
  public static healthCheck(): CancelablePromise<boolean> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/utils/health-check/",
    })
  }
}

// Emails module
export type TDataSendDashboard = {
  dashboard_id: string
  email: string
}

export class EmailsService {
  /**
   * Send Dashboard by ID to email
   * @returns Message Successful Response
   */
  public static sendDashboard(
    data: TDataSendDashboard,
  ): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/emails/send-dashboard",
      body: data,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }
}

export type TDataCreateCalendarEvent = {
  requestBody: CalendarEventCreate
}

export type TDataReadCalendarEvents = {
  limit?: number
  skip?: number
}

export class CalendarEventsService {
  /**
   * Отримати події
   */
  public static readCalendarEvents(): CancelablePromise<CalendarEventPublic[]> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/calendar/events",
    })
  }

  /**
   * Створити подію
   */
  public static createCalendarEvent(
    data: CalendarEventCreate,
  ): CancelablePromise<CalendarEventPublic> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/calendar/events",
      body: data,
      mediaType: "application/json",
    })
  }

  /**
   * Видалити подію
   */
  public static deleteCalendarEvent(eventId: string): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/calendar/events/${eventId}`,
    })
  }
}

export class MediaService {
  public static downloadImages(
    postId: string,
    data: FormData,
  ): Promise<{ url: string }[]> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/media/${postId}/images`,
      body: data,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  }

  public static downloadOneImage(data: FormData): CancelablePromise<string> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/media/images",
      body: data,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  }

  static deleteImage(
    postId: string,
    imageUrl: string,
  ): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/media/images/${postId}`,
      body: JSON.stringify({ imageUrl }), // Передаємо URL як JSON
      headers: {
        "Content-Type": "application/json",
      },
    })
  }

  static deleteImageInUrl(imageUrl: string): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/media/images/url",
      body: JSON.stringify({ imageUrl }), // Передаємо URL як JSON
      headers: {
        "Content-Type": "application/json",
      },
    })
  }
}

export type TDataCreateReport = {
  nazwa: string
}

export type TDataPatchReport = {
  report_id: string
  id: string
  maszyna: string
  field: string
  value: any
}
export type TDataRenameReport = {
  report_id: string
  nazwa: string
}
export type TDataCreateReportInfo = {
  report_id: string
  maszyna: string
  numer_zlecenia: string
  forma: string
  gwint: string
  gramatura: number
  kolor: string
}

export class ReportService {
  public static createReport(
    data: TDataCreateReport,
  ): CancelablePromise<ReportPublic> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reports/create",
      body: data,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  //закриття рапортів
  public static closeReport(): CancelablePromise<ReportPublic> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reports/close",
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  //отримання всіх рапортів /поранний
  public static getAllReport(): CancelablePromise<ReportPublic[]> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reports/get-all",
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static getReportById(id: string): CancelablePromise<ReportPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/reports/get/${id}`,
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static renameReport(
    data: TDataRenameReport,
  ): CancelablePromise<ReportPublic> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/reports/${data.report_id}/rename`,
      body: { nazwa: data.nazwa },
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static updateReportInfo(
    data: TDataPatchReport,
  ): CancelablePromise<ReportRowUpdate> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/report-info/",
      body: data,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static deleteReportInfo(id: string): CancelablePromise<ReportPublic> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/report-info/delete/${id}`,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static createReportInfo(
    data: TDataCreateReportInfo,
  ): CancelablePromise<ReportRowUpdate> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/report-info/create",
      body: data,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }
}

export type TDataGetOrders = {
  numer_zlecenia: string
  reports_id: string
}

export type TDataCreateBottle = {
  indeks?: string
  identyfikator?: string
  rodzaj_form?: string
  gwint?: string
  gramatura?: number
  pojemnosc?: number
  material?: string
  maszyna?: string
  koszt_maszyny_pl_h?: number
  ilosc_gniazd?: number
  czas_cyklu?: number
  wysokosc_butelki_3mm_wys_przekladki?: number
  ilosc_szt_pako_standart_p1?: number
  ilosc_szt_pol_przekladka_p3?: number
  ilosc_szt_karton_k1?: number
  ilosc_szt_karton_k2?: number
  ilosc_szt_karton_k3?: number
  ilosc_szt_karton_k5?: number
  ilosc_szt_worek_p4?: number
  ilosc_szt_karton_k6?: number
  rodzina?: string
  ksztalt_podstawy?: string
  gwint_gram?: string
}

export type TDataUpdateBottle = {
  indeks?: string
  identyfikator?: string
  rodzaj_form?: string
  gwint?: string
  gramatura?: number
  pojemnosc?: number
  material?: string
  maszyna?: string
  koszt_maszyny_pl_h?: number
  ilosc_gniazd?: number
  czas_cyklu?: number
  wysokosc_butelki_3mm_wys_przekladki?: number
  ilosc_szt_pako_standart_p1?: number
  ilosc_szt_pol_przekladka_p3?: number
  ilosc_szt_karton_k1?: number
  ilosc_szt_karton_k2?: number
  ilosc_szt_karton_k3?: number
  ilosc_szt_karton_k5?: number
  ilosc_szt_worek_p4?: number
  ilosc_szt_karton_k6?: number
  rodzina?: string
  ksztalt_podstawy?: string
  gwint_gram?: string
}

//при введенні номеру форми - отримуємо інші параметри

export class BottleService {
  public static listBottles(params?: {
    search?: string
    limit?: number
    offset?: number
  }): CancelablePromise<{ items: any[]; total: number }> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/bottles",
      query: params,
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static getBottleByID(id: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/bottles/${id}`,
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static createBottle(
    data: TDataCreateBottle,
  ): CancelablePromise<{ id?: string }> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/bottles",
      body: data,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static deleteBottle(
    id: string,
  ): CancelablePromise<{ deleted: boolean }> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/bottles/${id}`,
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static updateBottle(
    id: string,
    data: TDataUpdateBottle,
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/bottles/${id}`,
      body: data,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static getAllBottlesIdentyfication(params?: {
    search?: string
  }): CancelablePromise<{ identyfikators: string[] }> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/bottles/identyfikators",
      query: params,
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static getGwintsForIdentyfikator(
    identyfikator: string,
  ): CancelablePromise<{ gwints: string[] }> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/bottles/gwints",
      body: { identyfikator },
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static getGramaturaForIdentAndGwint(
    identyfikator: string,
    gwint: string,
  ): CancelablePromise<{ gramatura: string }> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/bottles/identyfikator-gwint",
      body: {
        identyfikator,
        gwint,
      },
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static getNumberOrdersForIdentyfikator(
    data: TDataGetOrders,
  ): CancelablePromise<ReportCopyInfo[]> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/zlecenie/get",
      body: data,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static addToData(): CancelablePromise<{ inserted: number }> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/bottles/add-to-data",
      errors: {
        422: "Validation Error",
      },
    })
  }
}

//Dasborad block
//Dasborad block

export type TDataDahboardID = {
  id: string
}

export type TDataDahboardRename = {
  id: string
  nazwa: string
}

export class DashboardService {
  //закриття дашборду
  public static closeDashcoard(data: TDataDahboardID): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/dashboard/closed",
      body: data,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  //зміна назви дашборду
  public static renameDashcoard(
    data: TDataDahboardRename,
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/dashboard/rename",
      body: data,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  //повернення актуального дашборду
  public static getActualDashcoard(): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/dashboard/actual",
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static getAllDashcoard(): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/dashboard/all",
      errors: {
        422: "Validation Error",
      },
    })
  }
}

//info dodatkowe

export type TDataDashboardExtraDateInfo = {
  dashboard_id: string
  info: string
}

export class DashboardExtraDate {
  //повернення даних до блоку інфо додаткове
  public static getInfoExtraDate(id: string): CancelablePromise<DashedInfo> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/dashboard-update/info/${id}`,
      errors: {
        422: "Validation Error",
      },
    })
  }

  //збереження даних / зміна даних
  public static updateDashboard(
    data: TDataDashboardExtraDateInfo,
  ): CancelablePromise<DashedInfo> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/dashboard-update/info",
      body: data,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }
}

//Дашборд особи додому
export type TDataDashboardPeopleHome = {
  dashboard_id: string
  ids: string[]
}
export type TPostOsobyDoDomuItem = {
  imie_nazwisko: string
  maszyna?: string
}

export type TPostOsobyDoDomuPayload = {
  dashboard_id: string
  items: TPostOsobyDoDomuItem[]
}

export class DashboardOsobyDoDomu {
  //отримання списку даних з блоку особи до дому
  public static getOsobyDoDomu(
    id: string,
  ): CancelablePromise<DashboardPeopleHome> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/dashboard-update/osoby-do-domu/${id}`,
      errors: {
        422: "Validation Error",
      },
    })
  }
  //видаення одної і більше осіб з даними до них

  public static deleteOsobyDoDomu(
    data: TDataDashboardPeopleHome,
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dashboard-update/osoby-do-domu",
      mediaType: "application/json",
      body: data,
      errors: {
        422: "Validation Error",
      },
    })
  }
  //створення блоку даних з інформацією( повернення ітема з блоком даних )
  public static postOsobyDoDomu(
    data: TPostOsobyDoDomuPayload,
  ): CancelablePromise<DashboardOsobyDoDomuResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/dashboard-update/osoby-do-domu",
      mediaType: "application/json",
      body: data,
      errors: { 422: "Validation Error" },
    })
  }
}

export type TDataZmianaWydmuchaOsobyNaZmianie = {
  dashboard_id: string
  kierownik?: string[]
  zastepca?: string
  jakosc?: string
  mechanik_wtrysk?: string[]
  mechanik_wydmuch?: string[]
  pomocnik_mechanik?: string[] // ← було: pomocnik_mechanika
  pomocnik_wtrysk?: string[]
  pomocnik_wydmuch?: string[]
  operator_umowa?: string[]
  operator_agencja?: string[] // ← було: operator_agengja
  magazynier?: string[]
}

export type TDataZmianaWtryskOsobyNaZmianie = {
  dashboard_id: string
  kierownik?: string[]
  mechanicy?: string[]
  elektrycy?: string[]
  pomocnicy?: string[]
}

export type TDataZmianaMagazynOsobyNaZmianie = {
  dashboard_id: string
  kierownik?: string // бекенд повертає string
  magazynier?: string[]
}

//працівники на зміні
export class DashboardOsobyNaZmianie {
  //видмух отримуємо список даних
  public static getOsobyNaZmianieWydmuch(
    id: string,
  ): CancelablePromise<WydmuchaOsobyNaZmianie> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/dashboard-update/pracownicy/wydmuch/${id}`,
      errors: {
        422: "Validation Error",
      },
    })
  }

  //втриск отримуємо список даних
  public static getOsobyNaZmianieWtysk(
    id: string,
  ): CancelablePromise<WtryskOsobyNaZmianie> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/dashboard-update/pracownicy/wtrysk/${id}`,
      errors: {
        422: "Validation Error",
      },
    })
  }

  //magazyn отримуємо список даних
  public static getOsobyNaZmianieMagazyn(
    id: string,
  ): CancelablePromise<MagazynOsobyNaZmianie> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/dashboard-update/pracownicy/magazyn/${id}`,
      errors: {
        422: "Validation Error",
      },
    })
  }

  //зміни даних/оновлення
  public static updateOsobyNaZmianieWydmuch(
    data: TDataZmianaWydmuchaOsobyNaZmianie,
  ): CancelablePromise<WydmuchaOsobyNaZmianie> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/dashboard-update/pracownicy/wydmuch",
      body: data,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static updateOsobyNaZmianieWtrysk(
    data: TDataZmianaWtryskOsobyNaZmianie,
  ): CancelablePromise<WtryskOsobyNaZmianie> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/dashboard-update/pracownicy/wtrysk",
      body: data,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static updateOsobyNaZmianieMagazyn(
    data: TDataZmianaMagazynOsobyNaZmianie,
  ): CancelablePromise<MagazynOsobyNaZmianie> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/dashboard-update/pracownicy/magazyn",
      body: data,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }
}

//DASHBOARD TABLE

export type TDataTableWydmuchWtryskItamse = {
  id: string
  indeks?: string
  uwagi?: string
  stan?: string
  maszyna?: string
}

export type TDataTableWydmuchWtrysk = {
  dashboard_id: string
  items: TDataTableWydmuchWtryskItamse[]
}

export class DashboardTableWydmuch {
  // отримання даних
  public static getTableWydmuch(
    id: string,
  ): CancelablePromise<DahboardWydmuchWtryskResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/dashboard-update/wydmuchy/${id}`,
      errors: {
        422: "Validation Error",
      },
    })
  }

  //зміна і оновлення даних видмух втриск таблиці дашборд
  public static updateTableWydmuch(
    data: TDataTableWydmuchWtrysk,
  ): CancelablePromise<DahboardWydmuchWtryskResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/dashboard-update/wydmuchy",
      body: data,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }
}

export class DashboardTableWtrysk {
  // отримання даних
  public static getTableWtrysk(
    id: string,
  ): CancelablePromise<DahboardWydmuchWtryskResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/dashboard-update/wtrysky/${id}`,
      errors: {
        422: "Validation Error",
      },
    })
  }

  //зміна і оновлення даних видмух втриск таблиці дашборд
  public static updateTableWtrysk(
    data: TDataTableWydmuchWtrysk,
  ): CancelablePromise<DahboardWydmuchWtryskResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/dashboard-update/wtrysky",
      body: data,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }
}

export class ParserService {
  public static downloadXML(data: FormData): CancelablePromise<string> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/parser/zlecenia",
      body: data,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  }

  public static listZlecenia(query?: {
    page?: number
    limit?: number
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/parser/zlecenia",
      query: {
        page: query?.page,
        limit: query?.limit,
      },
    })
  }
}

export type ZlecenieNumbersQuery = {
  q?: string
}

export type ZlecenieNumbersResponse = {
  items: ZlecenieSummaryDTO[]
  count: number
}

export class ZleceniaService {
  /**
   * Отримати список злецень (NrZlecenia) з короткою інформацією
   * Джерело: GET /v1/parser/zlecenia/numbers
   * Параметр q — опційний підрядковий фільтр по NrZlecenia
   */
  public static listNumbers(
    query?: ZlecenieNumbersQuery,
  ): CancelablePromise<ZlecenieNumbersResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/zlecenia/numbers",
      query: {
        q: query?.q,
      },
    })
  }

  // Деталі злеценія за ID
  // DTO, що відповідають бекенд-відповіді GET /v1/zlecenia/:id
}

export class ZleceniaDetailsService {
  /**
   * Отримати деталі злеценія за ID
   * Джерело: GET /v1/zlecenia/{id}
   */
  public static getById(id: string): CancelablePromise<ZlecenieDetailResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/zlecenia/${id}`,
    })
  }
}

export class ChangeoversService {
  public static getActiveChangeoversBulk(
    params: ChangeoverActiveBulkRequest,
  ): CancelablePromise<ChangeoverActiveBulkResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/changeovers/active/bulk",
      query: {
        machine_id: params.machine_ids,
      },
    })
  }

  public static startChangeover(
    data: ChangeoverStartRequest,
  ): CancelablePromise<ChangeoverResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/changeovers/start",
      body: data,
      mediaType: "application/json",
    })
  }

  public static getActiveChangeover(
    params: ChangeoverActiveRequest,
  ): CancelablePromise<ChangeoverResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/changeovers/active",
      query: {
        machine_id: params.machine_id,
      },
    })
  }

  public static updateStep(
    params: ChangeoverStepUpdateRequest,
  ): CancelablePromise<ChangeoverResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/changeovers/${params.changeover_id}/steps`,
      body: {
        step_id: params.step_id,
        completed: params.completed,
        signature: params.signature,
      },
      mediaType: "application/json",
    })
  }

  public static approveChangeover(
    params: ChangeoverApproveRequest,
  ): CancelablePromise<ChangeoverResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/changeovers/${params.changeover_id}/approve`,
    })
  }

  public static archiveChangeover(
    params: ChangeoverArchiveRequest,
  ): CancelablePromise<ChangeoverResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/changeovers/${params.changeover_id}/archive`,
    })
  }

  public static getHistory(
    params: ChangeoverActiveRequest,
  ): CancelablePromise<ChangeoverHistoryResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/changeovers/history",
      query: {
        machine_id: params.machine_id,
      },
    })
  }
}

export class QualityControlsService {
  public static startQualityControl(
    data: QualityControlStartRequest,
  ): CancelablePromise<QualityControlResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/quality-controls/start",
      body: data,
      mediaType: "application/json",
    })
  }

  public static getActiveQualityControl(
    params: QualityControlActiveRequest,
  ): CancelablePromise<QualityControlResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/quality-controls/active",
      query: {
        machine_id: params.machine_id,
      },
    })
  }

  public static updateItem(
    params: QualityControlUpdateItemRequest,
  ): CancelablePromise<QualityControlResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/quality-controls/${params.quality_control_id}/items`,
      body: {
        item_key: params.item_key,
        status: params.status,
      },
      mediaType: "application/json",
    })
  }

  public static updateMeta(
    params: QualityControlUpdateMetaRequest,
  ): CancelablePromise<QualityControlResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/quality-controls/${params.quality_control_id}/meta`,
      body: {
        shift_manager_name: params.shift_manager_name,
        quality_controller_name: params.quality_controller_name,
      },
      mediaType: "application/json",
    })
  }

  public static approve(
    params: QualityControlApproveRequest,
  ): CancelablePromise<QualityControlResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/quality-controls/${params.quality_control_id}/approve`,
    })
  }
}

export class NewProductionService {
  public static getActive(
    params: NewProductionActiveRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/new-production",
      query: {
        machine_id: params.machine_id,
      },
    })
  }

  public static getById(
    params: NewProductionGetByIdRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/new-production/${params.id}`,
    })
  }

  public static update(
    params: NewProductionUpdateRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/new-production/${params.id}`,
      body: {
        data: params.data,
      },
      mediaType: "application/json",
    })
  }

  public static approve(
    params: NewProductionApproveRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/new-production/${params.id}/approve`,
    })
  }
}

export class NewProductionAR710Service {
  public static getActive(
    params: NewProductionActiveRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/productions-ar-7-10",
      query: {
        machine_id: params.machine_id,
      },
    })
  }

  public static getById(
    params: NewProductionGetByIdRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/productions-ar-7-10/${params.id}`,
    })
  }

  public static update(
    params: NewProductionUpdateRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/productions-ar-7-10/${params.id}`,
      body: {
        data: params.data,
      },
      mediaType: "application/json",
    })
  }

  public static approve(
    params: NewProductionApproveRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/productions-ar-7-10/${params.id}/approve`,
    })
  }
}

export class NewProductionAR3Service {
  public static getActive(
    params: NewProductionActiveRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/productions-ar-3",
      query: {
        machine_id: params.machine_id,
      },
    })
  }

  public static getById(
    params: NewProductionGetByIdRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/productions-ar-3/${params.id}`,
    })
  }

  public static update(
    params: NewProductionUpdateRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/productions-ar-3/${params.id}`,
      body: {
        data: params.data,
      },
      mediaType: "application/json",
    })
  }

  public static approve(
    params: NewProductionApproveRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/productions-ar-3/${params.id}/approve`,
    })
  }
}

export class NewProductionAOKIService {
  public static getActive(
    params: NewProductionActiveRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/productions-aoki",
      query: {
        machine_id: params.machine_id,
      },
    })
  }

  public static getById(
    params: NewProductionGetByIdRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/productions-aoki/${params.id}`,
    })
  }

  public static update(
    params: NewProductionUpdateRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/productions-aoki/${params.id}`,
      body: {
        data: params.data,
      },
      mediaType: "application/json",
    })
  }

  public static approve(
    params: NewProductionApproveRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/productions-aoki/${params.id}/approve`,
    })
  }
}

export class NewProductionNisseiBigService {
  public static getActive(
    params: NewProductionActiveRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/new-production/nissei-big/active`,
      query: {
        machine_id: params.machine_id,
      },
    })
  }

  public static getById(
    params: NewProductionGetByIdRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/new-production/nissei-big/${params.id}`,
    })
  }

  public static update(
    params: NewProductionUpdateRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/new-production/nissei-big/${params.id}`,
      body: {
        data: params.data,
      },
      mediaType: "application/json",
    })
  }

  public static approve(
    params: NewProductionApproveRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/new-production/nissei-big/${params.id}/approve`,
    })
  }
}

export class NewProductionNisseiSmallService {
  public static getActive(
    params: NewProductionActiveRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/new-production/nissei-small/active`,
      query: {
        machine_id: params.machine_id,
      },
    })
  }

  public static getById(
    params: NewProductionGetByIdRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/new-production/nissei-small/${params.id}`,
    })
  }

  public static update(
    params: NewProductionUpdateRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/new-production/nissei-small/${params.id}`,
      body: {
        data: params.data,
      },
      mediaType: "application/json",
    })
  }

  public static approve(
    params: NewProductionApproveRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/new-production/nissei-small/${params.id}/approve`,
    })
  }
}

export class NewProductionOptimaService {
  public static getActive(
    params: NewProductionActiveRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/new-production/optima/active`,
      query: {
        machine_id: params.machine_id,
      },
    })
  }

  public static getById(
    params: NewProductionGetByIdRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/new-production/optima/${params.id}`,
    })
  }

  public static update(
    params: NewProductionUpdateRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/new-production/optima/${params.id}`,
      body: {
        data: params.data,
      },
      mediaType: "application/json",
    })
  }

  public static approve(
    params: NewProductionApproveRequest,
  ): CancelablePromise<NewProductionResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/new-production/optima/${params.id}/approve`,
    })
  }
}

export class MachinesService {
  public static getAllMachines(): CancelablePromise<MachinesResponceDTO[]> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/machines/get-all",
    })
  }
  public static getMachineByID(
    id: string,
  ): CancelablePromise<MachinesResponceDTO> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/machines/${id}`,
    })
  }

  /**
   * Створення машини
   * Підтримує 2 формати:
   * - JSON: коли немає зображення
   * - multipart/form-data: коли є файл зображення (поле "image")
   */
  public static createMachine(
    body:
      | FormData
      | {
          nazwa: string
          gniazda: number
          type?: string
          subtype?: string
          is_fixed?: boolean
          // extra_data приймає будь-який JSON-об'єкт або масив; на бекенді це datatypes.JSON
          extra_data?: any
        },
  ): CancelablePromise<MachinesResponceDTO> {
    const isFD = typeof FormData !== "undefined" && body instanceof FormData
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/machines/create",
      body: body as any,
      ...(isFD
        ? { headers: { "Content-Type": "multipart/form-data" } }
        : { mediaType: "application/json" }),
    })
  }

  public static patchMachine(
    id: string,
    body: {
      nazwa?: string
      gniazda?: number
      type?: string
      subtype?: string
      is_fixed?: boolean
      extra_data?: any
    },
  ): CancelablePromise<MachinesResponceDTO> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/machines/${id}`,
      body: body as any,
      mediaType: "application/json",
    })
  }

  public static patchMachineImage(
    id: string,
    body: FormData | { image: Blob },
  ): CancelablePromise<void> {
    const isFD = typeof FormData !== "undefined" && body instanceof FormData
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/machines/${id}/image`,
      body: body as any,
      ...(isFD
        ? { headers: { "Content-Type": "multipart/form-data" } }
        : { mediaType: "application/json" }),
    })
  }

  public static deleteMachine(id: string): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/machines/${id}`,
    })
  }
}

// WMS Сервіси які відповідають за товари і їх маніпуляція

// Product services
export class ProductService {
  public static getAllProducts(): CancelablePromise<ProductResponceDTO[]> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/products/get-all",
    })
  }

  public static getProductByID(
    id: string,
  ): CancelablePromise<ProductResponceDTO> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/products/${id}`,
    })
  }

  public static updateProductByID(
    id: string,
    data: ProductUpdateDTO,
  ): CancelablePromise<ProductResponceDTO> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/products/${id}`,
      body: data,
    })
  }

  public static createProduct(
    data: ProductCreateDTO,
  ): CancelablePromise<ProductResponceDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/products/create",
      body: data,
      mediaType: "application/json",
    })
  }

  public static deleteProduct(id: string): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/products/${id}`,
    })
  }

  public static getProductPriceRange(id: string): CancelablePromise<{
    last_purchase_price_netto?: number
    min_purchase_price_netto?: number
    max_purchase_price_netto?: number
    avg_purchase_price_netto?: number
    purchase_docs_count: number
    last_purchase_at?: string
  }> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/products/${id}/price-range`,
    })
  }

  public static getProductPurchasePriceHistory(
    id: string,
  ): CancelablePromise<ProductPurchasePricePointDTO[]> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/products/${id}/purchase-price-history`,
    })
  }
}

// Technology (recipes) services
export class TechnologyService {
  public static getAllTechnologies(
    search?: string,
  ): CancelablePromise<TechnologyResponseDTO[]> {
    const params: Record<string, any> = {}
    if (search && search.trim() !== "") params.search = search.trim()
    const query = new URLSearchParams(params as any).toString()
    const url = `/v1/technology/get-all${query ? `?${query}` : ""}`
    return __request(OpenAPI, {
      method: "GET",
      url,
    })
  }

  public static getTechnologyByID(
    id: string,
  ): CancelablePromise<TechnologyResponseDTO> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/technology/${id}`,
    })
  }

  public static createTechnology(
    data: TechnologyCreateDTO,
  ): CancelablePromise<TechnologyResponseDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/technology/create",
      body: data,
      mediaType: "application/json",
    })
  }

  public static updateTechnologyByID(
    id: string,
    data: TechnologyUpdateDTO,
  ): CancelablePromise<TechnologyResponseDTO> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/technology/${id}`,
      body: data,
      mediaType: "application/json",
    })
  }

  public static deleteTechnologyByID(
    id: string,
  ): CancelablePromise<{ status: string }> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/technology/${id}`,
    })
  }
}

// Production orders (zlecenia produkcyjne)
export class ProductionOrderService {
  public static getAllProductionOrders(
    search?: string,
    status?: string,
    technologyId?: string,
    outputProductId?: string,
  ): CancelablePromise<ProductionOrderListDTO[]> {
    const params: Record<string, any> = {}
    if (search && search.trim() !== "") params.search = search.trim()
    if (status && status.trim() !== "") params.status = status.trim()
    if (technologyId && technologyId.trim() !== "")
      params.technology_id = technologyId.trim()
    if (outputProductId && outputProductId.trim() !== "")
      params.output_product_id = outputProductId.trim()
    const query = new URLSearchParams(params as any).toString()
    const url = `/v1/production-orders/get-all${query ? `?${query}` : ""}`
    return __request(OpenAPI, {
      method: "GET",
      url,
    })
  }

  public static getProductionOrderByID(
    id: string,
  ): CancelablePromise<ProductionOrderResponseDTO> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/production-orders/${id}`,
    })
  }

  public static createProductionOrder(
    data: ProductionOrderCreateDTO,
  ): CancelablePromise<ProductionOrderResponseDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/production-orders/create",
      body: data,
      mediaType: "application/json",
    })
  }

  public static updateProductionOrderByID(
    id: string,
    data: ProductionOrderUpdateDTO,
  ): CancelablePromise<ProductionOrderResponseDTO> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/production-orders/${id}`,
      body: data,
      mediaType: "application/json",
    })
  }

  public static createMeldunek(
    id: string,
    data: MeldunekCreateDTO,
  ): CancelablePromise<MeldunekResponseDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/production-orders/${id}/meldunki`,
      body: data,
      mediaType: "application/json",
    })
  }

  public static getMeldunkiByOrder(
    id: string,
  ): CancelablePromise<MeldunekResponseDTO[]> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/production-orders/${id}/meldunki`,
    })
  }

  public static deleteMeldunek(
    orderId: string,
    meldunekId: string,
  ): CancelablePromise<{ status: string }> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/production-orders/${orderId}/meldunki/${meldunekId}`,
    })
  }

  public static updateMeldunek(
    orderId: string,
    meldunekId: string,
    data: MeldunekCreateDTO,
  ): CancelablePromise<MeldunekResponseDTO> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/production-orders/${orderId}/meldunki/${meldunekId}`,
      body: data,
      mediaType: "application/json",
    })
  }
}

// Product relations service
export class ProductRelationsService {
  /**
   * Get related documents for a product
   * Currently returns PZ (external receptions) only.
   * TODO: extend backend + client to support other document types (MM transfers, SO releases)
   * and optional filters (by type, date range).
   */
  public static getRelatedDocuments(
    productId: string,
  ): CancelablePromise<ProductRelatedDocDTO[]> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/products/${productId}/related-documents`,
    })
  }
}

// PZ Сервіс обробляємо прийняття товару на стан
export class ExReceptionsService {
  public static getAllExReceptions(
    year?: number,
    month?: number,
  ): CancelablePromise<ExReceptionsDTO[]> {
    const params: Record<string, any> = {}
    if (typeof year === "number") params.year = year
    if (typeof month === "number") params.month = month
    const query = new URLSearchParams(params as any).toString()
    const url = `/v1/external-receptions/get-all${query ? `?${query}` : ""}`
    return __request(OpenAPI, {
      method: "GET",
      url,
    })
  }

  public static getExReceptionByID(
    id: string,
  ): CancelablePromise<ExReceptionDTO> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/external-receptions/${id}`,
    })
  }

  public static createExReceptions(
    data: ExReceptionsCreateDTO,
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/external-receptions/create",
      body: data,
      mediaType: "application/json",
    })
  }

  public static updateExReceptionByID(
    id: string,
    data: ExReceptionsUpdateDTO,
  ): CancelablePromise<ExReceptionDTO> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/external-receptions/${id}`,
      body: data,
      mediaType: "application/json",
    })
  }

  public static scanExReceptionByID(
    id: string,
    data: ExReceptionScanDTO,
  ): CancelablePromise<ExReceptionScanResponseDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/external-receptions/${id}/scan`,
      body: data,
      mediaType: "application/json",
    })
  }

  public static confirmExReceptionByID(
    id: string,
  ): CancelablePromise<ExReceptionConfirmDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/external-receptions/${id}/confirm`,
    })
  }

  public static cancelExReceptionByID(
    id: string,
  ): CancelablePromise<ExReceptionConfirmDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/external-receptions/${id}/cancel`,
    })
  }
}

// WZ Сервіс обробляємо видання товару зовні
export class ExReleasesService {
  public static getAllExReleases(
    year?: number,
    month?: number,
  ): CancelablePromise<ExReleasesDTO[]> {
    const params: Record<string, any> = {}
    if (typeof year === "number") params.year = year
    if (typeof month === "number") params.month = month
    const query = new URLSearchParams(params as any).toString()
    const url = `/v1/external-releases/get-all${query ? `?${query}` : ""}`
    return __request(OpenAPI, {
      method: "GET",
      url,
    })
  }

  public static getExReleaseByID(id: string): CancelablePromise<ExReleaseDTO> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/external-releases/${id}`,
    })
  }

  public static createExRelease(
    data: ExReleasesCreateDTO,
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/external-releases/create",
      body: data,
      mediaType: "application/json",
    })
  }

  public static updateExReleaseByID(
    id: string,
    data: ExReleasesUpdateDTO,
  ): CancelablePromise<ExReleaseDTO> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/external-releases/${id}`,
      body: data,
      mediaType: "application/json",
    })
  }

  public static confirmExReleaseByID(
    id: string,
  ): CancelablePromise<ExReleaseConfirmDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/external-releases/${id}/confirm`,
    })
  }

  public static cancelExReleaseByID(
    id: string,
  ): CancelablePromise<ExReleaseConfirmDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/external-releases/${id}/cancel`,
    })
  }
}

// ZS Сервіс обробляємо замовлення продажу
export class SalesOrdersService {
  public static getAllSalesOrders(
    year?: number,
    month?: number,
  ): CancelablePromise<SalesOrdersDTO[]> {
    const params: Record<string, any> = {}
    if (typeof year === "number") params.year = year
    if (typeof month === "number") params.month = month
    const query = new URLSearchParams(params as any).toString()
    const url = `/v1/sales-orders/get-all${query ? `?${query}` : ""}`
    return __request(OpenAPI, {
      method: "GET",
      url,
    })
  }

  public static createSalesOrder(
    data: SalesOrdersCreateDTO,
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/sales-orders/create",
      body: data,
      mediaType: "application/json",
    })
  }

  public static getSalesOrderByID(
    id: string,
  ): CancelablePromise<SalesOrderDTO> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/sales-orders/${id}`,
    })
  }

  public static updateSalesOrderByID(
    id: string,
    data: SalesOrdersUpdateDTO,
  ): CancelablePromise<SalesOrderDTO> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/sales-orders/${id}`,
      body: data,
      mediaType: "application/json",
    })
  }

  public static confirmSalesOrderByID(
    id: string,
  ): CancelablePromise<SalesOrderConfirmDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/sales-orders/${id}/confirm`,
    })
  }

  public static cancelSalesOrderByID(
    id: string,
  ): CancelablePromise<SalesOrderConfirmDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/sales-orders/${id}/cancel`,
    })
  }

  public static createReleaseFromSalesOrder(
    id: string,
  ): CancelablePromise<ExReleaseConfirmDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/sales-orders/${id}/create-wz`,
    })
  }
}

// Секція складів
export class WareHouseService {
  public static getAllWarehouses(): CancelablePromise<WarehousesDTO[]> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/warehouse/get-all",
    })
  }

  public static getWarehouseByID(
    id: string,
    inStockOnly?: boolean,
    releaseId?: string,
    search?: string,
  ): CancelablePromise<WarehouseResponceDTO> {
    const params: Record<string, string> = {}
    if (inStockOnly) params.in_stock = "1"
    if (releaseId) params.release_id = releaseId
    if (search?.trim()) params.search = search.trim()
    const query = new URLSearchParams(params).toString()
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/warehouse/${id}${query ? `?${query}` : ""}`,
    })
  }

  public static getWarehouseLocations(
    id: string,
    search?: string,
  ): CancelablePromise<{ items: string[] }> {
    const params = new URLSearchParams()
    if (search?.trim()) params.set("search", search.trim())
    const query = params.toString()
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/warehouse/${id}/locations${query ? `?${query}` : ""}`,
    })
  }

  public static createWarehouse(
    data: WarehouseCreateDTO,
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/warehouse/create",
      body: data,
      mediaType: "application/json",
    })
  }

  public static listLocationTemplates(): CancelablePromise<
    WarehouseLocationTemplateDTO[]
  > {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/warehouse/location-templates",
    })
  }

  public static createLocationTemplate(
    data: WarehouseLocationTemplateCreateDTO,
  ): CancelablePromise<WarehouseLocationTemplateDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/warehouse/location-templates",
      body: data,
      mediaType: "application/json",
    })
  }

  public static updateLocationTemplate(
    templateId: string,
    data: WarehouseLocationTemplateUpdateDTO,
  ): CancelablePromise<WarehouseLocationTemplateDTO> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/warehouse/location-templates/${templateId}`,
      body: data,
      mediaType: "application/json",
    })
  }

  public static deleteLocationTemplate(
    templateId: string,
  ): CancelablePromise<{ status: string }> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/warehouse/location-templates/${templateId}`,
    })
  }

  public static updateWarehouse(
    warehouseId: string,
    data: UpdateWarehouseDTO,
  ): CancelablePromise<WarehouseResponceDTO> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/warehouse/${warehouseId}`,
      body: data,
      mediaType: "application/json",
    })
  }

  public static updateStockLocation(
    warehouseId: string,
    data: {
      product_id: string
      from_location_code: string
      location_code: string
    },
  ): CancelablePromise<{ status: string }> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/warehouse/${warehouseId}/stock/location`,
      body: data,
      mediaType: "application/json",
    })
  }

  public static updateProductMinQty(
    warehouseId: string,
    data: { product_id: string; min_qty: number },
  ): CancelablePromise<{ status: string }> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/warehouse/${warehouseId}/stock/min-qty`,
      body: data,
      mediaType: "application/json",
    })
  }

  public static searchWarehouse(q: string): CancelablePromise<{
    query: string
    products: import("./models").WarehouseProductSearchHitDTO[]
    meldunki: import("./models").WarehouseMeldunekSearchHitDTO[]
  }> {
    const query = new URLSearchParams({ q }).toString()
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/warehouse/search?${query}`,
    })
  }
}

export class TransferService {
  public static getMmpDiscrepancyReport(
    year?: number,
    month?: number,
    dateFrom?: string,
    dateTo?: string,
    status?: string,
    reason?: string,
    warehouseId?: string,
  ): CancelablePromise<MmpDiscrepancyReportDTO> {
    const params: Record<string, any> = {}
    if (typeof year === "number") params.year = year
    if (typeof month === "number") params.month = month
    if (dateFrom) params.date_from = dateFrom
    if (dateTo) params.date_to = dateTo
    if (status) params.status = status
    if (reason) params.reason = reason
    if (warehouseId) params.warehouse_id = warehouseId
    const query = new URLSearchParams(params as any).toString()
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/transfers/reports/mmp-discrepancies${query ? `?${query}` : ""}`,
    })
  }

  public static getAllDocumentsMMW(
    year?: number,
    month?: number,
  ): CancelablePromise<TransfersDocumentMMWDTO[]> {
    const params: Record<string, any> = {}
    if (typeof year === "number") params.year = year
    if (typeof month === "number") params.month = month
    const query = new URLSearchParams(params as any).toString()
    const url = `/v1/transfers/MMW/get-all${query ? `?${query}` : ""}`
    return __request(OpenAPI, {
      method: "GET",
      url: url,
    })
  }

  public static createDocumentMMW(
    data: TransfersCreateMMW,
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/transfers/MMW/create",
      body: data,
      mediaType: "application/json",
    })
  }

  public static updateDocumentMMWByID(
    id: string,
    data: TransfersUpdateMMW,
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/transfers/MMW/${id}`,
      body: data,
      mediaType: "application/json",
    })
  }

  public static getDocumentMMWByID(
    id: string,
  ): CancelablePromise<TransferDocumentMMWByIDDTO> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/transfers/MMW/${id}`,
    })
  }

  public static confirmDocumentMMWByID(id: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/transfers/MMW/${id}/confirm`,
    })
  }

  public static cancelDocumentMMWByID(id: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/transfers/MMW/${id}/cancel`,
    })
  }

  public static getAllDocumentsMMP(
    year?: number,
    month?: number,
  ): CancelablePromise<TransfersDocumentMMPDTO[]> {
    const params: Record<string, any> = {}
    if (typeof year === "number") params.year = year
    if (typeof month === "number") params.month = month
    const query = new URLSearchParams(params as any).toString()
    const url = `/v1/transfers/MMP/get-all${query ? `?${query}` : ""}`
    return __request(OpenAPI, {
      method: "GET",
      url,
    })
  }

  public static getDocumentMMPByID(
    id: string,
  ): CancelablePromise<TransferDocumentMMWByIDDTO> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/transfers/MMP/${id}`,
    })
  }

  public static confirmDocumentMMPByID(
    id: string,
    data: TransferConfirmMMPDTO,
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/transfers/MMP/${id}/confirm`,
      body: data,
      mediaType: "application/json",
    })
  }

  public static cancelDocumentMMPByID(id: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/transfers/MMP/${id}/cancel`,
    })
  }

  public static getAllDocumentsMMR(
    year?: number,
    month?: number,
  ): CancelablePromise<TransfersDocumentMMRDTO[]> {
    const params: Record<string, any> = {}
    if (typeof year === "number") params.year = year
    if (typeof month === "number") params.month = month
    const query = new URLSearchParams(params as any).toString()
    const url = `/v1/transfers/MMR/get-all${query ? `?${query}` : ""}`
    return __request(OpenAPI, {
      method: "GET",
      url,
    })
  }

  public static getDocumentMMRByID(
    id: string,
  ): CancelablePromise<TransferDocumentMMWByIDDTO> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/transfers/MMR/${id}`,
    })
  }

  public static confirmDocumentMMRByID(id: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/transfers/MMR/${id}/confirm`,
    })
  }
}

export class InternalExpenditureService {
  public static getAllDocumentsRW(
    year?: number,
    month?: number,
  ): CancelablePromise<InternalExpenditureDocumentRWDTO[]> {
    const params: Record<string, any> = {}
    if (typeof year === "number") params.year = year
    if (typeof month === "number") params.month = month
    const query = new URLSearchParams(params as any).toString()
    const url = `/v1/internal-expenditure/RW/get-all${query ? `?${query}` : ""}`
    return __request(OpenAPI, {
      method: "GET",
      url: url,
    })
  }

  public static getDocumentRWByID(
    id: string,
  ): CancelablePromise<InternalExpenditureDocumentRWDetailDTO> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/internal-expenditure/RW/${id}`,
    })
  }

  public static createDocumentRW(
    data: InternalExpenditureCreateRW,
  ): CancelablePromise<InternalExpenditureDocumentRWResponseDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/internal-expenditure/RW/create",
      body: data,
      mediaType: "application/json",
    })
  }

  public static updateDocumentRWByID(
    id: string,
    data: InternalExpenditureUpdateRW,
  ): CancelablePromise<InternalExpenditureDocumentRWResponseDTO> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/internal-expenditure/RW/${id}`,
      body: data,
      mediaType: "application/json",
    })
  }

  public static confirmDocumentRWByID(
    id: string,
  ): CancelablePromise<InternalExpenditureDocumentRWResponseDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/internal-expenditure/RW/${id}/confirm`,
    })
  }

  public static unconfirmDocumentRWByID(
    id: string,
  ): CancelablePromise<InternalExpenditureDocumentRWResponseDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/internal-expenditure/RW/${id}/unconfirm`,
    })
  }
}

export class InternalRevenueService {
  public static getAllDocumentsPW(
    year?: number,
    month?: number,
  ): CancelablePromise<InternalRevenueDocumentPWDTO[]> {
    const params: Record<string, any> = {}
    if (typeof year === "number") params.year = year
    if (typeof month === "number") params.month = month
    const query = new URLSearchParams(params as any).toString()
    const url = `/v1/internal-revenue/PW/get-all${query ? `?${query}` : ""}`
    return __request(OpenAPI, {
      method: "GET",
      url: url,
    })
  }

  public static getDocumentPWByID(
    id: string,
  ): CancelablePromise<InternalRevenueDocumentPWDetailDTO> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/internal-revenue/PW/${id}`,
    })
  }

  public static createDocumentPW(
    data: InternalRevenueCreatePW,
  ): CancelablePromise<InternalRevenueDocumentPWResponseDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/internal-revenue/PW/create",
      body: data,
      mediaType: "application/json",
    })
  }

  public static updateDocumentPWByID(
    id: string,
    data: InternalRevenueUpdatePW,
  ): CancelablePromise<InternalRevenueDocumentPWResponseDTO> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/internal-revenue/PW/${id}`,
      body: data,
      mediaType: "application/json",
    })
  }

  public static confirmDocumentPWByID(
    id: string,
  ): CancelablePromise<InternalRevenueDocumentPWResponseDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/internal-revenue/PW/${id}/confirm`,
    })
  }

  public static cancelDocumentPWByID(
    id: string,
  ): CancelablePromise<InternalRevenueDocumentPWResponseDTO> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/internal-revenue/PW/${id}/cancel`,
    })
  }
}
